import dayjs from 'dayjs';
import { MRT_ColumnDef, MRT_RowData } from 'mantine-react-table';
import { ReactNode } from 'react';
import { QUEUE_EMPTY_COLOR, QUEUE_WARN_COLOR, TwoLineCell } from '../../components/QueueCells';

interface ConsultRowLike extends MRT_RowData {
  consult?: { start?: string; status?: string; created?: string };
  nextStep?: string;
  /** No-show tab only. */
  missCount?: number;
}

const CONSULT_SIZE = 136;
const MISSED_CONSULT_SIZE = 116;
const UPCOMING_CONSULT_SIZE = 104;

/** A consult's date, `· No-show` in amber or `· Canceled` in grey when it was missed. */
function consultLine(consult: ConsultRowLike['consult']): ReactNode {
  return (
    <>
      {consult?.start ? dayjs(consult.start).format('MMM D') : '—'}
      {consult?.status === 'noshow' && <span style={{ color: QUEUE_WARN_COLOR, fontWeight: 600 }}> · No-show</span>}
      {consult?.status === 'cancelled' && <span style={{ color: QUEUE_EMPTY_COLOR }}> · Canceled</span>}
    </>
  );
}

/** The consult's date over its chart-note outcome. */
export function consultColumn<Row extends ConsultRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'consult',
    header: 'Consult',
    size: CONSULT_SIZE,
    accessorFn: (row) => row?.consult?.start ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const outcome = row.original?.nextStep;
      return (
        <TwoLineCell
          primary={consultLine(row.original?.consult)}
          secondary={outcome ?? <span style={{ color: QUEUE_EMPTY_COLOR }}>No chart note</span>}
        />
      );
    },
  };
}

/** An upcoming consult's date over when it was booked. */
export function upcomingConsultColumn<Row extends ConsultRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'consult',
    header: 'Consult',
    size: UPCOMING_CONSULT_SIZE,
    accessorFn: (row) => row?.consult?.start ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const created = row.original?.consult?.created;
      return (
        <TwoLineCell
          primary={consultLine(row.original?.consult)}
          secondary={created ? `Booked ${dayjs(created).format('MMM D')}` : undefined}
        />
      );
    },
  };
}

/** The latest missed consult over how many the patient has missed, amber from the second. */
export function missedConsultColumn<Row extends ConsultRowLike>(): MRT_ColumnDef<Row> {
  return {
    id: 'consult',
    header: 'Consult',
    size: MISSED_CONSULT_SIZE,
    accessorFn: (row) => row?.consult?.start ?? '',
    sortingFn: 'datetime',
    Cell: ({ row }) => {
      const misses = row.original?.missCount ?? 0;
      return (
        <TwoLineCell
          primary={consultLine(row.original?.consult)}
          secondary={misses > 0 ? `${misses} ${misses === 1 ? 'miss' : 'misses'}` : undefined}
          secondaryEmphasis={misses > 1 ? 'warning' : undefined}
        />
      );
    },
  };
}
